import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { Form, Input, Modal, Radio, Spin, Typography, Upload, Select, Button } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { UploadOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import { encode } from 'blurhash';

import componentContent from './GameEditModal.content.json';
import useTranslate from '@hooks/useTranslate';
import modals, { closeModal, IModalsState } from '@stores/modals';
import IGame from '@interfaces/IGame';
import Tag from '@interfaces/Tag';
import games, {
  createGame,
  updateGame,
  checkGameCode,
  resetCodeValidation,
  getItem,
} from '@stores/games';
import gamesRequest, { approve } from '@stores/gamesRequest/gamesRequest';
import css from './GameEditModal.module.less';
import GameEditModalPlatforms from './GameEditModalPlatforms';
import { getInitLocale, LOCALE_BY_DATA } from './GameEditModal.constants';
import { LOCALE, LOCALE_NAME } from '@stores/localeWeight';
import gameGenres from '@constants/gameGenres';
import { checkGameGenreTag, createGameGenreTag } from '@helpers/gameGenres';
import {
  formatIgdbIds,
  getIgdbIdsRequestValue,
  splitIgdbIds,
  validationRules,
} from './GameEditModal.helpers';
import useGuards from '@hooks/useGuards';
import { verifyUser } from '@stores/users';

const FormItem = Form.Item;
const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 18 },
};

const MAX_GENRES_COUNT = 3;

type GameFormValues = Omit<Partial<IGame>, 'igdbIds'> & {
  igdbIds?: string[];
};

type GenreOption = {
  value: string;
  label: string;
};

const loadImage = async (file: File) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    const fileReader = new FileReader();
    fileReader.onload = () => {
      img.src = fileReader.result as string;
    };
    fileReader.readAsDataURL(file);
  });
};

const getImageData = (image: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  if (context) {
    context.drawImage(image, 0, 0);
    return context.getImageData(0, 0, image.width, image.height);
  }
};

const encodeImageToBlurhash = async (file: File) => {
  const image = await loadImage(file);
  const imageData = getImageData(image);
  if (imageData) {
    return encode(imageData.data, imageData.width, imageData.height, 5, 5);
  }
};

const onFinish = async (
  values: GameFormValues,
  game: IGame,
  isChangeOfficialUser: boolean,
  isAdmin: boolean,
) => {
  const localEn = values.gameLocales?.find((games) => games.locale === LOCALE.EN);
  const localOver = values.gameLocales?.filter((games) => games.locale !== LOCALE.EN);
  let hash = '';
  if (localEn?.photo?.file) {
    hash = (await encodeImageToBlurhash(localEn?.photo?.file?.originFileObj)) as string;
  }
  const formData: Record<string, unknown> = {
    code: values.code,
    name: localEn?.name,
    description: values.description,
    tags: values.tags,
    picHashCode: hash === '' ? game?.picHashCode : hash,
    synonyms: values.synonyms,
    platforms: values.platforms,
    gender: values.gender,
    gameLocaleIncomeDtos: localOver?.map((l) => ({
      name: l.name,
      locale: l.locale,
    })),
    officialAccountId: values?.officialAccountId,
    appleStoreUrl: values?.appleStoreUrlOrExternal,
    googlePlayUrl: values?.googlePlayUrlOrExternal,
  };
  const igdbIds = getIgdbIdsRequestValue(values.igdbIds);
  if (isAdmin && igdbIds !== undefined) {
    formData.igdbIds = igdbIds;
  }
  const imagesFormData: FormData[] = [];
  values.gameLocales?.forEach((locale) => {
    const picture = get(locale, ['photo', 'file', 'originFileObj'], null);
    if (picture) {
      const imageFormData = new FormData();
      imageFormData.append('image', picture);
      imageFormData.append('imageHashCode', values.picHashCode as string);
      imageFormData.append('locale', locale.locale);
      if (game?.id) {
        imageFormData.append('id', game?.id);
      }
      imagesFormData.push(imageFormData);
    }
  });

  const genreOptions = values.tags as unknown as GenreOption[];

  if (game?.id) {
    formData.id = game?.id;
    await updateGame({
      id: game.id,
      gameData: JSON.stringify(formData),
      imagesData: imagesFormData,
      tags: genreOptions ? genreOptionsToGenreTags(genreOptions) : null,
    });
    if (isChangeOfficialUser)
      await verifyUser({
        userId: String(formData.officialAccountId),
        data: { isOfficial: true },
        preloaderId: String(formData.officialAccountId),
      });
  } else {
    await createGame({
      gameData: JSON.stringify(formData),
      imagesData: imagesFormData,
      tags: genreOptions ? genreOptionsToGenreTags(genreOptions) : null,
    });
    if (isChangeOfficialUser)
      await verifyUser({
        userId: String(formData.officialAccountId),
        data: { isOfficial: true },
        preloaderId: String(formData.officialAccountId),
      });
  }
};

const genreTagToGenreOption = (tag: Tag): GenreOption => {
  const { value } = tag;
  return {
    value: value,
    label: value,
  };
};

const genreTagsToGenreOptions = (tags: Tag[]) => {
  return tags.reduce((acc, tag) => {
    const isValidGenreTag = checkGameGenreTag(tag);
    if (isValidGenreTag) {
      acc.push(genreTagToGenreOption(tag));
    }
    return acc;
  }, [] as GenreOption[]);
};

const genreToGenreOption = (genre: string): GenreOption => {
  return {
    value: genre,
    label: genre,
  };
};

const genresToGenreOptions = (genres: string[]): GenreOption[] => {
  return genres.map(genreToGenreOption);
};

const genreOptionsToGenreTags = (options: GenreOption[]): Tag[] => {
  return options.map((option, i) => {
    const { value } = option;
    return createGameGenreTag(value, i + 1);
  }) as Tag[];
};

const filterGenreOptions = (options: GenreOption[]): GenreOption[] => {
  if (options.length >= MAX_GENRES_COUNT) {
    return [];
  }
  const genresRest = gameGenres.filter(
    (genre) => !options.some((option) => option.value === genre),
  );
  return genresToGenreOptions(genresRest);
};

const mapDataToFieldsValue = (data: IGame) => {
  const localeEn = {
    name: data.name,
    locale: LOCALE.EN,
  };
  const localeOver = LOCALE_BY_DATA.map((locale) => {
    const gameLocale = (data.gameLocales ?? []).find((game) => game.locale === locale);
    if (gameLocale) {
      return { ...gameLocale };
    }
    return { locale };
  });

  return {
    picHashCode: data.picHashCode,
    code: data?.code,
    officialAccountId: data?.officialAccount?.id,
    appleStoreUrlOrExternal: data.appleStoreUrlOrExternal,
    googlePlayUrlOrExternal: data.googlePlayUrlOrExternal,
    synonyms: data.synonyms || [],
    igdbIds: formatIgdbIds(data.igdbIds),
    gender: data.gender,
    platforms: data.platforms,
    gameLocales: [localeEn, ...localeOver],
    tags: genreTagsToGenreOptions(data.tags || []),
  };
};

type GameModalData = IGame & {
  isRequest?: boolean;
  shouldApproveAfterSubmit?: boolean;
  suggestId?: number;
};

const GameEditModal: FunctionComponent<unknown> = () => {
  const { isAdmin } = useGuards();
  const content = useTranslate(componentContent);
  const {
    visibleState: { game: visible },
    data,
  } = useStore<IModalsState<IGame>>(modals);
  const modalData = data as GameModalData;
  const { preloaders, codeValidationResult } = useStore(games);
  const { preloaders: preloadersRequest } = useStore(gamesRequest);
  const [form] = useForm();
  const [imagesGameLocale, setImagesGameLocale] = useState<Record<LOCALE, string>>(getInitLocale());
  const [moreLocales, setMoreLocales] = useState<boolean>(false);
  const [genreOptionsFiltered, setGenreOptionsFiltered] = useState<GenreOption[]>([]);
  const [isChangeOfficialUser, setIsChangeOfficialUser] = useState<boolean>(false);
  const [igdbSearch, setIgdbSearch] = useState('');

  useEffect(() => {
    let isActive = true;
    setIgdbSearch('');
    if (visible && data) {
      resetCodeValidation();
      const fieldsValue = mapDataToFieldsValue(data);
      setGenreOptionsFiltered(filterGenreOptions(fieldsValue.tags));
      form.setFieldsValue(fieldsValue);
      form.setFields([{ name: 'igdbIds', touched: false }]);
      if (data.code) checkGameCode({ code: data.code });
      if (data.id) {
        getItem(data.id)
          .then((response) => {
            const igdbIds = response?.data?.body?.igdbIds;
            if (isActive && !form.isFieldTouched('igdbIds') && igdbIds !== undefined) {
              form.setFieldsValue({ igdbIds: formatIgdbIds(igdbIds) });
            }
          })
          .catch(() => {});
      }
    } else {
      setGenreOptionsFiltered(genresToGenreOptions(gameGenres));
      form.resetFields();
    }
    setImagesGameLocale(getInitLocale());
    return () => {
      isActive = false;
    };
  }, [visible, data, form]);

  useEffect(() => {
    const imageGame: Record<LOCALE, string> = getInitLocale();
    if (data?.gameLocales?.length > 0) {
      data.gameLocales.forEach((game) => {
        imageGame[game.locale] = game?.image?.url || '';
      });
    }
    if (data?.image?.url) {
      imageGame[LOCALE.EN] = data?.image?.url;
    }
    setImagesGameLocale(imageGame);
  }, [data]);

  const localeItem = useCallback(
    ({ name, key }) => {
      const locale = form.getFieldValue(['gameLocales', name, 'locale']) as LOCALE;
      return (
        <div
          key={key}
          style={{ display: 'flex', alignItems: 'baseline' }}
        >
          <div className={css.locale}>{LOCALE_NAME[locale]}</div>
          <FormItem
            name={[name, 'name']}
            className={css.name}
            rules={[
              {
                required: locale === LOCALE.EN,
                message: content.name_message,
              },
            ]}
          >
            <Input
              onBlur={(event) => {
                const name = event.target.value;
                checkGameCode({ code: name }).then((result) => {
                  if (!!result?.body && result?.status?.statusCode === 'OK') {
                    form.setFieldsValue({ code: result.body.code });
                  }
                });
              }}
            />
          </FormItem>

          <FormItem
            name={[name, 'photo']}
            className={css.photo}
          >
            <Upload
              listType='picture-card'
              showUploadList={false}
              customRequest={({ file, onSuccess }) => {
                setImagesGameLocale((value) => {
                  return {
                    ...value,
                    [locale]: URL.createObjectURL(file as Blob),
                  };
                });
                setTimeout(() => onSuccess && onSuccess({ statusText: 'ok' }), 0);
              }}
            >
              {imagesGameLocale[locale] ? (
                <img
                  className={css.image}
                  src={imagesGameLocale[locale]}
                  alt=''
                />
              ) : (
                <UploadOutlined />
              )}
            </Upload>
          </FormItem>
        </div>
      );
    },
    [content.name_message, form, imagesGameLocale],
  );

  const localeList = useCallback(
    (fields) => {
      if (!moreLocales) {
        return (
          <>
            {fields.slice(0, 1).map((field: any, index: number) => {
              return (
                <>
                  {localeItem(field)}
                  <Button
                    type='link'
                    onClick={() => {
                      setMoreLocales(true);
                    }}
                  >
                    {content.moreLocales}
                  </Button>
                </>
              );
            })}
          </>
        );
      }
      return (
        <>
          {fields.map((field: any, index: number) => {
            return localeItem(field);
          })}
          <Button
            type='link'
            onClick={() => {
              setMoreLocales(false);
            }}
          >
            {content.lessLocales}
          </Button>
        </>
      );
    },
    [content.lessLocales, content.moreLocales, localeItem, moreLocales],
  );

  const closeModalHandler = useCallback(() => {
    setMoreLocales(false);
    closeModal({ name: 'game' });
  }, []);

  const handleChangeGenres = (optionsSelected: GenreOption[]) => {
    setGenreOptionsFiltered(filterGenreOptions(optionsSelected));
  };

  const isCodeValid = (code?: string) => {
    if (modalData?.id && code === modalData.code) return true;

    return !!(
      codeValidationResult?.valid &&
      (codeValidationResult?.occupiedByGame === modalData?.id ||
        !codeValidationResult?.occupiedByGame)
    );
  };

  const handleSubmit = async (values: GameFormValues) => {
    if (!isCodeValid(values.code)) return;

    if (modalData?.isRequest && modalData?.shouldApproveAfterSubmit && values?.code) {
      await approve({ suggestId: Number(modalData.suggestId), code: values.code });
      closeModal({ name: 'game' });
    } else {
      await onFinish(values, data, isChangeOfficialUser, isAdmin);
    }
  };

  const submitForm = () => {
    if (isAdmin && igdbSearch.trim()) {
      const igdbIds: string[] = form.getFieldValue('igdbIds') || [];
      form.setFieldsValue({ igdbIds: [...igdbIds, ...splitIgdbIds(igdbSearch)] });
      setIgdbSearch('');
    }
    form.submit();
  };

  if (!visible) return null;
  return (
    <Form
      onFinish={handleSubmit}
      form={form}
      {...layout}
      initialValues={{
        gender: 'MALE',
        gameLocales: [LOCALE.EN, ...LOCALE_BY_DATA].map((locale) => ({ locale })),
      }}
    >
      <Modal
        title={content.title}
        okButtonProps={{
          htmlType: 'submit',
          disabled: !isCodeValid(form.getFieldValue('code')),
        }}
        confirmLoading={
          !!preloaders.createGame ||
          !!preloaders.updateGame ||
          !!preloaders.checkGameCode ||
          !!preloadersRequest.approve
        }
        visible
        onCancel={() => closeModalHandler()}
        onOk={submitForm}
        width={800}
      >
        <Spin
          spinning={
            !!preloaders.createGame ||
            !!preloaders.updateGame ||
            !!preloaders.checkGameCode ||
            !!preloadersRequest.approve
          }
        >
          {data ? (
            <FormItem label='id'>
              <Typography.Text type='secondary'>{data.id}</Typography.Text>
            </FormItem>
          ) : null}

          <FormItem
            label={content.code}
            name='code'
            validateStatus={
              codeValidationResult
                ? codeValidationResult?.valid &&
                  (codeValidationResult?.occupiedByGame === modalData?.id ||
                    !codeValidationResult?.occupiedByGame)
                  ? 'success'
                  : 'error'
                : undefined
            }
          >
            <Input
              placeholder={content.code}
              disabled={!isAdmin}
              onBlur={async (event) => {
                const code = event.target.value;
                const result = await checkGameCode({ code });
                if (result?.status?.statusCode === 'OK' && result.body.code) {
                  form.setFieldsValue({ code: result.body.code });
                }
              }}
            />
          </FormItem>

          <FormItem
            label={content.igdbIds}
            name='igdbIds'
            rules={[
              {
                validator: (_, igdbIds: string[] = []) => {
                  const hasInvalidId = igdbIds.some(
                    (igdbId) =>
                      !/^\d+$/.test(igdbId) ||
                      !Number.isSafeInteger(Number(igdbId)) ||
                      Number(igdbId) <= 0,
                  );
                  return hasInvalidId
                    ? Promise.reject(new Error(content.igdbIdsValidation))
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode='tags'
              style={{ width: '100%' }}
              placeholder={content.igdbIdsPlaceholder}
              disabled={!isAdmin}
              allowClear
              tokenSeparators={[',', ' ']}
              searchValue={igdbSearch}
              onSearch={(value) => {
                setIgdbSearch(value);
                if (value) form.setFields([{ name: 'igdbIds', touched: true }]);
              }}
              onChange={() => setIgdbSearch('')}
            />
          </FormItem>

          <FormItem
            label={content.official}
            name='officialAccountId'
          >
            <Input
              placeholder={content.official}
              disabled={!isAdmin}
              onChange={(event) =>
                setIsChangeOfficialUser(event.target.value !== data?.officialAccount?.id)
              }
            />
          </FormItem>

          <FormItem
            label={content.appleLink}
            name='appleStoreUrlOrExternal'
            rules={[validationRules.appleStoreUrlOrExternal.base]}
          >
            <Input placeholder={content.links} />
          </FormItem>

          <FormItem
            label={content.googleLink}
            name='googlePlayUrlOrExternal'
            rules={[validationRules.googlePlayUrlOrExternal.base]}
          >
            <Input placeholder={content.links} />
          </FormItem>

          <FormItem
            label={content.synonyms}
            name='synonyms'
          >
            <Select
              mode='tags'
              style={{ width: '100%' }}
              placeholder={content.synonyms}
            />
          </FormItem>

          <FormItem
            label={content.genres}
            name='tags'
          >
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              labelInValue
              placeholder={content.genres}
              options={genreOptionsFiltered}
              onChange={handleChangeGenres}
            />
          </FormItem>

          <FormItem
            label={content.gender}
            name='gender'
          >
            <Radio.Group>
              {(['MALE', 'FEMALE'] as IGame['gender'][]).map((gender) => (
                <Radio.Button
                  value={gender}
                  key={gender}
                >
                  {content[gender]}
                </Radio.Button>
              ))}
            </Radio.Group>
          </FormItem>

          <FormItem
            label={content.platforms}
            name='platforms'
          >
            <GameEditModalPlatforms />
          </FormItem>

          <div style={{ display: 'flex', marginBottom: 8, alignItems: 'baseline' }}>
            <div className={css.locale}>{content.locale}</div>
            <div className={css.name}>{content.name}</div>
            <div className={css.photo}>{content.picture}</div>
          </div>
          <Form.List name='gameLocales'>{(fields) => <>{localeList(fields)}</>}</Form.List>
        </Spin>
      </Modal>
    </Form>
  );
};

export default GameEditModal;
